package com.fantasytenis.web.screens.profile

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.web.components.AvatarDisplay
import com.fantasytenis.web.components.AvatarSize
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.h2Style
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Profile Screen - View-only display of user profile.
 * Links to Account Settings for editing.
 */
@Composable
fun ProfileScreen(
    onNavigateToSettings: () -> Unit
) {
    val currentUser by SharedData.currentUser.collectAsState()

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            padding(32.px)
            maxWidth(600.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
        }
    }) {
        // Header with back navigation
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
                marginBottom(32.px)
            }
        }) {
            A(
                href = "#",
                attrs = {
                    onClick { e ->
                        e.preventDefault()
                        NavigationState.navigate(Route.Home)
                    }
                    style {
                        color(Colors.TextSecondary)
                        fontSize(24.px)
                        property("text-decoration", "none")
                    }
                }
            ) {
                Text("\u2190")
            }
            H2({
                style {
                    h2Style()
                    margin(0.px)
                }
            }) {
                Text("Profile")
            }
        }

        currentUser?.let { user ->
            // Profile content centered
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                }
            }) {
            // Avatar
            Div({
                style {
                    marginBottom(24.px)
                }
            }) {
                AvatarDisplay(
                    avatarUrl = user.avatarUrl,
                    initials = user.initials,
                    size = AvatarSize.XLARGE
                )
            }

            // Username or Name
            H2({
                style {
                    h2Style()
                    marginBottom(8.px)
                    textAlign("center")
                }
            }) {
                Text(user.displayName)
            }

            // Full name (if showing username)
            if (user.hasUsername) {
                P({
                    style {
                        fontSize(16.px)
                        color(Colors.TextSecondary)
                        marginBottom(8.px)
                        textAlign("center")
                    }
                }) {
                    Text(user.fullName)
                }
            }

            // Email
            P({
                style {
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                    marginBottom(32.px)
                    textAlign("center")
                }
            }) {
                Text(user.email)
            }

            // Divider
            Div({
                style {
                    width(100.percent)
                    height(1.px)
                    backgroundColor(Colors.Border)
                    marginBottom(32.px)
                }
            }) {}

            // Profile Info Cards
            Div({
                style {
                    width(100.percent)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                    marginBottom(32.px)
                }
            }) {
                ProfileInfoCard(
                    label = "Email",
                    value = user.email
                )
                ProfileInfoCard(
                    label = "Name",
                    value = user.fullName
                )
                user.username?.let { username ->
                    ProfileInfoCard(
                        label = "Username",
                        value = "@$username"
                    )
                }
                user.phoneNumber?.let { phone ->
                    ProfileInfoCard(
                        label = "Phone",
                        value = phone
                    )
                }
                ProfileInfoCard(
                    label = "KYC Status",
                    value = when {
                        user.isKycVerified -> "Verified"
                        else -> "Pending"
                    },
                    valueColor = if (user.isKycVerified) Colors.Success else Colors.Warning
                )
            }

            // Account Settings Button
            Button(
                text = "Account Settings",
                onClick = onNavigateToSettings,
                fullWidth = true
            )
            } // End of centered content Div
        } ?: run {
            // Loading or not logged in
            P({
                style {
                    color(Colors.TextSecondary)
                    textAlign("center")
                }
            }) {
                Text("Loading profile...")
            }
        }
    }
}

@Composable
private fun ProfileInfoCard(
    label: String,
    value: String,
    valueColor: CSSColorValue = Colors.TextPrimary
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(8.px)
            padding(16.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        Span({
            style {
                fontSize(14.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text(label)
        }
        Span({
            style {
                fontSize(14.px)
                fontWeight(500)
                color(valueColor)
            }
        }) {
            Text(value)
        }
    }
}
